import QRCode from "qrcode";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AsesorField,
  AsesorFieldReq,
  ClientUserData,
  createAsesorAPI,
  CreateAsesorRequest,
  getAsesorAPI,
  getAsesorBrandConfigAPI,
  getAsesoresByCodeAPI,
  getAsesorFieldsByAsesorCodeAPI,
  getAsesorPicAPI,
  getBrandConfigByAsesorCodeAPI,
} from "../hyperFlow/hyperFlowApi";
import { RootState } from "../magic_forms_2/store/store";
import { AttachedFile, sendQRAsesor } from "./asesorAPI";

export interface AsesorState {
  brandConfig: BrandConfig;
  asesor: ClientUserData;
  loadingAsesor: LoadingStatus;
  loadingAsesores: LoadingStatus;
  loadingCreateAsesor: LoadingStatus;
  asesorCreateRequest: CreateAsesorRequest;
  generatedURlForQR: string;
  clientUserId: string;
  asesorPicBase64: string;
  loadingSendEmail: LoadingStatus;
  loadingAsesorPic: LoadingStatus;
  loadingBrandConfig: LoadingStatus;
  asesorFields: AsesorField[];
  loadingAsesorFields: LoadingStatus;
  asesores: ClientUserData[];
}

export type LoadingStatus = "idle" | "pending" | "resolved" | "rejected";

export interface BrandConfig {
  backgroundColor1?: string | null;
  backgroundColor2?: string | null;
  logoUrl?: string | null;
  darkMode?: boolean;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  tertiaryTextColor?: string;
  quaternaryTextColor?: string;
  quinaryTextColor?: string;
  senaryTextColor?: string;
  errorTextColor?: string;
  primaryButtonColor?: string;
  secondaryButtonColor?: string;
  tertiaryButtonColor?: string;
  quaternaryButtonColor?: string;
  primaryMessageColor?: string;
  secondaryMessageColor?: string;
  bubbleDescColor?: string;
  primaryButtonTextColor?: string;
  secondaryButtonTextColor?: string;
  accentColor?: string;
  modalBackgroundColor?: string;
  modalTextColor?: string;
  modalButtonColor?: string;
  modalBackdropColor?: string;
  barraShow?: boolean;
  backgroundImage?: string | null;
  xeiaActivado?: boolean;
  xeiaChatActivado?: boolean;
  font?: string | null;
}

const initialState: AsesorState = {
  brandConfig: {
    backgroundColor1: "#013b69",
    backgroundColor2: "#000e1a",
    logoUrl: "https://cdn.anteia.co/logos/anteia_white_transparent.png",
    primaryButtonColor:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%)",
    primaryButtonTextColor: "#ffffff",
    primaryTextColor: "#ffffff",
  },
  asesor: {
    active: false,
    asesorData: {
      cargo: "",
      codAsesor: "",
      direccion: "",
      oficina: "",
      photoUrl: "",
    },
    clientId: "",
    email: "",
    esAsesor: false,
    id: "",
    identification: "",
    name: "",
    phoneNumber: "",
    userId: "",
  },

  loadingAsesor: "idle",
  loadingCreateAsesor: "idle",
  generatedURlForQR: "",
  asesorCreateRequest: {
    code: "",
    fillFields: [],
    photoBase64: "",
    registrationId: "",
    authId: "",
  },
  clientUserId: "",
  loadingSendEmail: "idle",
  asesorPicBase64: "",
  loadingAsesorPic: "idle",
  loadingBrandConfig: "idle",
  asesorFields: [],
  loadingAsesorFields: "idle",
  loadingAsesores: "idle",
  asesores: [],
};

export const createAsesor = createAsyncThunk(
  "asesor/createAsesor",
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const asesorCreateRequest = state.asesor.asesorCreateRequest;
    const response = await createAsesorAPI(asesorCreateRequest);
    if (response.error) {
      return rejectWithValue(response.error);
    }
    return response;
  }
);

export const getAsesorInfo = createAsyncThunk(
  "asesor/getAsesorInfo",
  async (asesorId: string, { rejectWithValue }) => {
    const response = await getAsesorAPI(asesorId);
    if (response.error) {
      return rejectWithValue(response.error);
    }
    return response;
  }
);

export const getAsesorBrandConfig = createAsyncThunk(
  "asesor/getAsesorBrandConfig",
  async (asesorId: string, { rejectWithValue }) => {
    const response = await getAsesorBrandConfigAPI(asesorId);
    if (response.error || response.brandConfigResponse === null) {
      return rejectWithValue(response.error);
    }
    return response.brandConfigResponse;
  }
);

export const getBrandConfigByCode = createAsyncThunk(
  "asesor/getBrandConfigByCode",
  async (code: string, { rejectWithValue }) => {
    const response = await getBrandConfigByAsesorCodeAPI(code);
    if (response.error || response.brandConfigResponse === null) {
      return rejectWithValue(response.error);
    }
    return response.brandConfigResponse;
  }
);

export const getAsesorFieldsByCode = createAsyncThunk(
  "asesor/getAsesorFieldsByCode",
  async (code: string, { rejectWithValue }) => {
    const response = await getAsesorFieldsByAsesorCodeAPI(code);
    if (response.error || response.asesorFieldsResponse === null) {
      return rejectWithValue(response.error);
    }
    return response.asesorFieldsResponse;
  }
);

export const getAsesoresByCode = createAsyncThunk(
  "asesor/getAsesoresByCode",
  async (code: string, { rejectWithValue }) => {
    const response = await getAsesoresByCodeAPI(code);
    if (response.error || response.asesorsResponse === null) {
      return rejectWithValue(response.error);
    }
    return response.asesorsResponse;
  }
);

export const getAsesorPic = createAsyncThunk(
  "asesor/getAsesorPic",
  async (asesorId: string, { rejectWithValue }) => {
    const response = await getAsesorPicAPI(asesorId);
    if (response.error) {
      return rejectWithValue(response.error);
    }
    return response;
  }
);

export const sendEmail = createAsyncThunk(
  "asesor/sendEmail",
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const registrationId = state.asesor.asesorCreateRequest.registrationId;
    const generatedURlForQR = state.asesor.generatedURlForQR;
    const qrCodeDataUrl = await QRCode.toDataURL(generatedURlForQR, {
      width: 400,
    });
    let attachedFiles: AttachedFile[] = [];

    if (qrCodeDataUrl.includes("data:image/png;base64,")) {
      let attachedFile: AttachedFile = {
        base64Content: qrCodeDataUrl.split("data:image/png;base64,")[1],
        fileName: "QRCodeAsesor.png",
        mimeType: "image/png",
      };
      attachedFiles.push(attachedFile);
    }

    const response = await sendQRAsesor({
      registrationId: registrationId,
      content: "",
      subject: "",
      templateData: {
        bannerTitle: "Tu código de Asesor",
        title: "Bien hecho! Tu código QR ha sido creado",
        paragraph1:
          "Hemos generado un código QR para que puedas compartirlo con tus clientes",
        paragraph2:
          "Tu código QR está en forma de imagen adjunto a este correo, puedes descargarlo, imprimirlo, incrustarlo en tus redes sociales o simplemente compartirlo con tus clientes",
      },
      attachedFiles,
    });
    if (response.result) {
      return response;
    } else {
      return rejectWithValue("error sending email");
    }
  }
);

export const asesorSlice = createSlice({
  name: "asesor",
  initialState,
  reducers: {
    changeCreateAsesorRequest: (state, action) => {
      state.asesorCreateRequest = action.payload;
    },
    resetCurrentAsesor: (state) => {
      state.asesor = {
        active: false,
        asesorData: {
          codAsesor: "",
          direccion: "",
          oficina: "",
          photoUrl: "",
          cargo: "",
        },
        clientId: "",
        email: "",
        esAsesor: false,
        id: "",
        identification: "",
        name: "",
        phoneNumber: "",
        userId: "",
      };
      state.loadingAsesor = "idle";
      state.loadingAsesorFields = "idle";
      state.loadingAsesorPic = "idle";
    },
    resetAsesorDirectory: (state) => {
      state.loadingAsesores = "idle";
      state.asesores = [];
    },
  },
  extraReducers: (builder) => {
    // create asesor
    builder
      .addCase(createAsesor.pending, (state) => {
        state.loadingCreateAsesor = "pending";
      })
      .addCase(createAsesor.fulfilled, (state, action) => {
        if (action.payload.asesorResponse) {
          state.generatedURlForQR = action.payload.asesorResponse.url;
          state.clientUserId = action.payload.asesorResponse.clientUserId;
          state.asesorCreateRequest.registrationId =
            action.payload.asesorResponse.registrationId;
        }
        state.loadingCreateAsesor = "resolved";
      })
      .addCase(createAsesor.rejected, (state) => {
        state.loadingCreateAsesor = "rejected";
      });

    // sending email
    builder
      .addCase(sendEmail.pending, (state) => {
        state.loadingSendEmail = "pending";
      })
      .addCase(sendEmail.fulfilled, (state) => {
        state.loadingSendEmail = "resolved";
      })
      .addCase(sendEmail.rejected, (state) => {
        state.loadingSendEmail = "rejected";
      });

    // getAsesor

    builder
      .addCase(getAsesorInfo.pending, (state) => {
        state.loadingAsesor = "pending";
      })
      .addCase(getAsesorInfo.fulfilled, (state, action) => {
        state.loadingAsesor = "resolved";
        state.asesor = action.payload.asesorResponse!;
      })
      .addCase(getAsesorInfo.rejected, (state) => {
        state.loadingAsesor = "rejected";
      });

    // getAsesorPic
    builder
      .addCase(getAsesorPic.pending, (state) => {
        state.loadingAsesorPic = "pending";
      })
      .addCase(getAsesorPic.rejected, (state) => {
        state.loadingAsesorPic = "rejected";
      })
      .addCase(getAsesorPic.fulfilled, (state, action) => {
        state.loadingAsesorPic = "resolved";
        state.asesorPicBase64 = action.payload.asesorResponse?.imgBase64!;
      });

    // asesorBrandConfig
    builder
      .addCase(getAsesorBrandConfig.pending, (state) => {
        state.loadingBrandConfig = "pending";
      })
      .addCase(getAsesorBrandConfig.rejected, (state) => {
        state.loadingBrandConfig = "rejected";
      })
      .addCase(getAsesorBrandConfig.fulfilled, (state, action) => {
        state.loadingBrandConfig = "resolved";
        state.brandConfig = action.payload;
      });

    builder
      .addCase(getBrandConfigByCode.pending, (state) => {
        state.loadingBrandConfig = "pending";
      })
      .addCase(getBrandConfigByCode.rejected, (state) => {
        state.loadingBrandConfig = "rejected";
      })
      .addCase(getBrandConfigByCode.fulfilled, (state, action) => {
        state.loadingBrandConfig = "resolved";
        state.brandConfig = action.payload;
      });

    // asesorFields
    builder
      .addCase(getAsesorFieldsByCode.pending, (state) => {
        state.loadingAsesorFields = "pending";
      })
      .addCase(getAsesorFieldsByCode.rejected, (state) => {
        state.loadingAsesorFields = "rejected";
      })
      .addCase(getAsesorFieldsByCode.fulfilled, (state, action) => {
        state.loadingAsesorFields = "resolved";
        state.asesorFields = action.payload.sort(
          (a, b) => a.showingOrder!! - b.showingOrder!!
        );
        let fillFields: AsesorFieldReq[] = state.asesorFields.map(
          (asesorField) => {
            return {
              id: asesorField.fieldId ?? "",
              value: asesorField.defaultValue ?? "",
            };
          }
        );
        state.asesorCreateRequest.fillFields = fillFields;
      });
    // get multiple Asesores
    builder
      .addCase(getAsesoresByCode.pending, (state) => {
        state.loadingAsesores = "pending";
      })
      .addCase(getAsesoresByCode.fulfilled, (state, action) => {
        state.loadingAsesores = "resolved";
        // assign only the asesores that asesorData is not null
        state.asesores = action.payload!.filter((asesor) => asesor.asesorData);
      })
      .addCase(getAsesoresByCode.rejected, (state) => {
        state.loadingAsesores = "rejected";
      });
  },
});

export const {
  changeCreateAsesorRequest,
  resetAsesorDirectory,
  resetCurrentAsesor,
} = asesorSlice.actions;

export const selectAsesor = (state: RootState) => state.asesor.asesor;
export const selectBrandConfig = (state: RootState) => state.asesor.brandConfig;
export const selectLoadingAsesor = (state: RootState) =>
  state.asesor.loadingAsesor;

export const selectAsesorCreateRequest = (state: RootState) =>
  state.asesor.asesorCreateRequest;
export const selectGeneratedURlForQR = (state: RootState) =>
  state.asesor.generatedURlForQR;
export const selectLoadingCreateAsesor = (state: RootState) =>
  state.asesor.loadingCreateAsesor;
export const selectClientUserId = (state: RootState) =>
  state.asesor.clientUserId;
export const selectLoadingSendEmail = (state: RootState) =>
  state.asesor.loadingSendEmail;
export const selectAsesorPic = (state: RootState) =>
  state.asesor.asesorPicBase64;
export const selectAsesorPicLoading = (state: RootState) =>
  state.asesor.loadingAsesorPic;
export const selectLoadingBrandConfig = (state: RootState) =>
  state.asesor.loadingBrandConfig;
export const selectLoadingAsesorFields = (state: RootState) =>
  state.asesor.loadingAsesorFields;
export const selectAsesorFields = (state: RootState) =>
  state.asesor.asesorFields;
export const selectLoadingAsesores = (state: RootState) =>
  state.asesor.loadingAsesores;
export const selectAsesores = (state: RootState) => state.asesor.asesores;
export default asesorSlice.reducer;
